import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X } from 'lucide-react';

import {
  VideoConfig, QuizConfig, FlashcardConfig, FillGapsConfig,
  MatchingConfig, CaseStudyConfig, TextContentConfig,
  ReflectionJournalConfig, AudioConfig, DownloadableConfig,
  DragDropTaskConfig, ImageHotspotConfig, TrainerTipConfig,
  TimelineConfig, LabelDiagramConfig, SortOrderConfig,
  DecisionTreeConfig, ProgressCheckpointConfig,
} from './configs';

const ElementPropertiesPanel = ({ element, module, onUpdate, onClose }) => {
  if (!element) {
    return (
      <div className="w-80 border-l border-slate-200 bg-white p-6 flex items-center justify-center" data-testid="properties-panel">
        <div className="text-center text-slate-500">
          <p className="text-sm">Select an element to edit its properties</p>
        </div>
      </div>
    );
  }

  const renderConfigEditor = () => {
    switch (element.type) {
      case 'video': return <VideoConfig config={element.config} onChange={onUpdate} />;
      case 'quiz': return <QuizConfig config={element.config} onChange={onUpdate} />;
      case 'flashcard': return <FlashcardConfig config={element.config} onChange={onUpdate} />;
      case 'fill_gaps': return <FillGapsConfig config={element.config} onChange={onUpdate} />;
      case 'matching': return <MatchingConfig config={element.config} onChange={onUpdate} />;
      case 'case_study': return <CaseStudyConfig config={element.config} onChange={onUpdate} />;
      case 'text': return <TextContentConfig config={element.config} onChange={onUpdate} />;
      case 'reflection_journal': return <ReflectionJournalConfig config={element.config} onChange={onUpdate} />;
      case 'audio': return <AudioConfig config={element.config} onChange={onUpdate} />;
      case 'downloadable': return <DownloadableConfig config={element.config} onChange={onUpdate} />;
      case 'drag_drop_task': return <DragDropTaskConfig config={element.config} onChange={onUpdate} />;
      case 'image_hotspot': return <ImageHotspotConfig config={element.config} onChange={onUpdate} />;
      case 'trainer_tip': return <TrainerTipConfig config={element.config} onChange={onUpdate} />;
      case 'timeline': return <TimelineConfig config={element.config} onChange={onUpdate} />;
      case 'label_diagram': return <LabelDiagramConfig config={element.config} onChange={onUpdate} />;
      case 'sort_order': return <SortOrderConfig config={element.config} onChange={onUpdate} />;
      case 'decision_tree': return <DecisionTreeConfig config={element.config} onChange={onUpdate} />;
      case 'progress_checkpoint': return <ProgressCheckpointConfig config={element.config} onChange={onUpdate} />;
      default: return <p className="text-sm text-slate-500">Configuration not available</p>;
    }
  };

  return (
    <div className="w-96 border-l border-slate-200 bg-white flex flex-col" data-testid="properties-panel">
      <div className="flex items-center justify-between p-4 border-b border-slate-200">
        <h3 className="font-semibold text-slate-900">Element Properties</h3>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              value={element.title || ''}
              onChange={(e) => onUpdate({ title: e.target.value })}
              placeholder="Element title"
            />
          </div>

          <div className="space-y-2">
            <Label>Duration (minutes)</Label>
            <Input
              type="number"
              min="0"
              value={element.duration_minutes || ''}
              onChange={(e) => onUpdate({ duration_minutes: parseInt(e.target.value) || null })}
              placeholder="Auto-calculated"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label>Required</Label>
            <Switch
              checked={element.is_required !== false}
              onCheckedChange={(v) => onUpdate({ is_required: v })}
            />
          </div>

          <div className="border-t pt-4">
            <h4 className="font-medium text-sm text-slate-900 mb-3 capitalize">
              {element.type.replace(/_/g, ' ')} Settings
            </h4>
            {/* key forces a fresh editor per element so content never leaks between elements */}
            <div key={element.id}>{renderConfigEditor()}</div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};

export default ElementPropertiesPanel;
